/**
 * EmailConnectionEditorModal - Full-screen modal for creating/editing email connections.
 * Design cohérent avec AgentEditorModal.
 * Focus IMAP uniquement (pas Gmail/Outlook OAuth).
 */
import { useState, useCallback, useEffect, useId } from 'react'
import { X, Save, Loader2, AlertTriangle, Mail, Server, Lock, Clock, Settings, Bot, CheckCircle } from 'lucide-react'
import { useCreateEmailConnection, useUpdateEmailConnection } from '../../hooks/useEmailConnections'
import type { EmailConnection, EmailConnectionCreate, EmailConnectionUpdate } from '../../types/email'
import { api } from '../../lib/api'

interface EmailConnectionEditorModalProps {
  connection?: EmailConnection | null
  preselectedAgentId?: number | null
  onClose: () => void
}

interface Agent {
  id: number
  name: string
  description?: string
}

interface FormData {
  email_address: string
  display_name: string
  agent_id: number | null
  imap_host: string
  imap_port: string
  smtp_host: string
  smtp_port: string
  username: string
  password: string
  use_tls: boolean
  check_interval_minutes: string
  enabled: boolean
  auto_reply_enabled: boolean
}

const DEFAULT_FORM: FormData = {
  email_address: '',
  display_name: '',
  agent_id: null,
  imap_host: '',
  imap_port: '993',
  smtp_host: '',
  smtp_port: '587',
  username: '',
  password: '',
  use_tls: true,
  check_interval_minutes: '5',
  enabled: true,
  auto_reply_enabled: false,
}

export function EmailConnectionEditorModal({ connection, preselectedAgentId, onClose }: EmailConnectionEditorModalProps) {
  const formId = useId()
  const isEditMode = !!connection
  
  const createConnection = useCreateEmailConnection()
  const updateConnection = useUpdateEmailConnection()
  
  const [formData, setFormData] = useState<FormData>(DEFAULT_FORM)
  const [agents, setAgents] = useState<Agent[]>([])
  const [loadingAgents, setLoadingAgents] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [testing, setTesting] = useState(false)
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null)

  // Load agents
  useEffect(() => {
    const loadAgents = async () => {
      setLoadingAgents(true)
      try {
        const { data } = await api.get('/agents')
        const items: Agent[] = Array.isArray(data?.items) ? data.items : []
        setAgents(items)
        
        // Pré-sélectionner l'agent si fourni
        if (preselectedAgentId && !isEditMode) {
          setFormData(prev => ({ ...prev, agent_id: preselectedAgentId }))
        }
      } catch (err) {
        console.error('Failed to load agents:', err)
      } finally {
        setLoadingAgents(false)
      }
    }
    loadAgents()
  }, [preselectedAgentId, isEditMode])

  // Initialize form with existing connection data
  useEffect(() => {
    if (connection) {
      setFormData({
        email_address: connection.email_address,
        display_name: connection.display_name || '',
        agent_id: connection.agent_id || null,
        imap_host: connection.imap_host || '',
        imap_port: String(connection.imap_port || 993),
        smtp_host: connection.smtp_host || '',
        smtp_port: String(connection.smtp_port || 587),
        username: '',
        password: '',
        use_tls: connection.use_tls ?? true,
        check_interval_minutes: String(connection.check_interval_minutes || 5),
        enabled: connection.enabled,
        auto_reply_enabled: connection.auto_reply_enabled,
      })
    }
  }, [connection])

  const handleChange = (field: keyof FormData, value: string | boolean | number | null) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    setError(null)
  }

  const handleTestConnection = useCallback(async () => {
    setTesting(true)
    setTestResult(null)
    setError(null)
    
    try {
      const { data } = await api.post('/email/test-connection', {
        imap_host: formData.imap_host,
        imap_port: parseInt(formData.imap_port),
        username: formData.username,
        password: formData.password,
        use_tls: formData.use_tls,
      })
      
      setTestResult({
        success: data.success,
        message: data.message,
      })
    } catch (err: any) {
      const message = err.response?.data?.detail || err.message || 'Erreur lors du test'
      setTestResult({
        success: false,
        message,
      })
    } finally {
      setTesting(false)
    }
  }, [formData])

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault()
    
    // Validation
    if (!formData.email_address.trim()) {
      setError('L\'adresse email est requise')
      return
    }
    if (!formData.imap_host.trim()) {
      setError('Le serveur IMAP est requis')
      return
    }
    if (!isEditMode && !formData.username.trim()) {
      setError('Le nom d\'utilisateur est requis')
      return
    }
    if (!isEditMode && !formData.password.trim()) {
      setError('Le mot de passe est requis')
      return
    }

    setSaving(true)
    setError(null)

    try {
      if (isEditMode && connection) {
        const updateData: EmailConnectionUpdate & { id: number } = {
          id: connection.id,
          display_name: formData.display_name || undefined,
          agent_id: formData.agent_id || undefined,
          imap_host: formData.imap_host,
          imap_port: parseInt(formData.imap_port) || 993,
          smtp_host: formData.smtp_host || undefined,
          smtp_port: parseInt(formData.smtp_port) || 587,
          use_tls: formData.use_tls,
          check_interval_minutes: parseInt(formData.check_interval_minutes) || 5,
          enabled: formData.enabled,
          auto_reply_enabled: formData.auto_reply_enabled,
        }
        
        // Only include credentials if provided
        if (formData.username.trim()) {
          updateData.username = formData.username
        }
        if (formData.password.trim()) {
          updateData.password = formData.password
        }
        
        await updateConnection.mutateAsync(updateData)
      } else {
        const createData: EmailConnectionCreate = {
          email_address: formData.email_address,
          display_name: formData.display_name || undefined,
          provider: 'imap',
          agent_id: formData.agent_id || undefined,
          imap_host: formData.imap_host,
          imap_port: parseInt(formData.imap_port) || 993,
          smtp_host: formData.smtp_host || formData.imap_host,
          smtp_port: parseInt(formData.smtp_port) || 587,
          username: formData.username,
          password: formData.password,
          use_tls: formData.use_tls,
        }
        
        await createConnection.mutateAsync(createData)
      }
      
      onClose()
    } catch (err: any) {
      const detail = err?.response?.data?.detail || err?.message || 'Erreur lors de la sauvegarde'
      setError(typeof detail === 'object' ? JSON.stringify(detail) : detail)
    } finally {
      setSaving(false)
    }
  }, [formData, isEditMode, connection, createConnection, updateConnection, onClose])

  // ESC to close
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [onClose])

  return (
    <div 
      className="fixed inset-0 z-50 bg-zinc-950"
      role="dialog"
      aria-modal="true"
      aria-labelledby={`${formId}-title`}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-800 bg-zinc-900">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-emerald-900/30">
            <Mail className="w-5 h-5 text-emerald-400" />
          </div>
          <h2 id={`${formId}-title`} className="text-lg font-semibold text-white">
            {isEditMode ? `Modifier: ${connection?.email_address}` : 'Nouvelle connexion IMAP'}
          </h2>
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg border border-zinc-700 text-zinc-300 hover:bg-zinc-800 transition-colors"
          >
            Annuler
          </button>
          <button
            type="submit"
            form={formId}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white font-medium transition-colors disabled:opacity-50"
            disabled={saving}
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Sauvegarde...
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                {isEditMode ? 'Mettre à jour' : 'Créer'}
              </>
            )}
          </button>
        </div>
      </div>

      {/* Error banner */}
      {error && (
        <div className="mx-6 mt-4 p-3 rounded-lg bg-red-900/20 border border-red-800 text-red-300 flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Form Content */}
      <form id={formId} onSubmit={handleSubmit} className="h-[calc(100vh-73px)] overflow-y-auto">
        <div className="max-w-4xl mx-auto px-6 py-8">
          <div className="grid gap-8 md:grid-cols-2">
            
            {/* Section: Identité */}
            <section className="space-y-6">
              <div className="flex items-center gap-2 text-emerald-400 mb-4">
                <Mail className="w-5 h-5" />
                <h3 className="text-sm font-semibold uppercase tracking-wider">Identité</h3>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-2">
                  Adresse email *
                </label>
                <input
                  type="email"
                  value={formData.email_address}
                  onChange={(e) => handleChange('email_address', e.target.value)}
                  disabled={isEditMode}
                  placeholder="contact@votredomaine.com"
                  className="w-full px-4 py-3 rounded-xl bg-zinc-800 border border-zinc-700 text-white placeholder-zinc-500 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 transition disabled:opacity-50 disabled:cursor-not-allowed"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-2">
                  Nom d'affichage
                </label>
                <input
                  type="text"
                  value={formData.display_name}
                  onChange={(e) => handleChange('display_name', e.target.value)}
                  placeholder="Support Client"
                  className="w-full px-4 py-3 rounded-xl bg-zinc-800 border border-zinc-700 text-white placeholder-zinc-500 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 transition"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-2">
                  Agent IA
                </label>
                <select
                  value={formData.agent_id || ''}
                  onChange={(e) => handleChange('agent_id', e.target.value ? parseInt(e.target.value) : null)}
                  disabled={loadingAgents}
                  className="w-full px-4 py-3 rounded-xl bg-zinc-800 border border-zinc-700 text-white focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 transition disabled:opacity-50"
                >
                  <option value="">Aucun agent</option>
                  {agents.map((agent) => (
                    <option key={agent.id} value={agent.id}>
                      {agent.name}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-zinc-500">
                  L'agent générera les réponses automatiques aux emails
                </p>
              </div>
            </section>

            {/* Section: Serveur IMAP */}
            <section className="space-y-6">
              <div className="flex items-center gap-2 text-emerald-400 mb-4">
                <Server className="w-5 h-5" />
                <h3 className="text-sm font-semibold uppercase tracking-wider">Serveur IMAP</h3>
              </div>
              
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-zinc-300 mb-2">
                    Hôte IMAP *
                  </label>
                  <input
                    type="text"
                    value={formData.imap_host}
                    onChange={(e) => handleChange('imap_host', e.target.value)}
                    placeholder="imap.votredomaine.com"
                    className="w-full px-4 py-3 rounded-xl bg-zinc-800 border border-zinc-700 text-white placeholder-zinc-500 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 transition"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-zinc-300 mb-2">
                    Port
                  </label>
                  <input
                    type="number"
                    value={formData.imap_port}
                    onChange={(e) => handleChange('imap_port', e.target.value)}
                    placeholder="993"
                    className="w-full px-4 py-3 rounded-xl bg-zinc-800 border border-zinc-700 text-white placeholder-zinc-500 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 transition"
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-zinc-300 mb-2">
                    Hôte SMTP
                  </label>
                  <input
                    type="text"
                    value={formData.smtp_host}
                    onChange={(e) => handleChange('smtp_host', e.target.value)}
                    placeholder="smtp.votredomaine.com"
                    className="w-full px-4 py-3 rounded-xl bg-zinc-800 border border-zinc-700 text-white placeholder-zinc-500 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 transition"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-zinc-300 mb-2">
                    Port
                  </label>
                  <input
                    type="number"
                    value={formData.smtp_port}
                    onChange={(e) => handleChange('smtp_port', e.target.value)}
                    placeholder="587"
                    className="w-full px-4 py-3 rounded-xl bg-zinc-800 border border-zinc-700 text-white placeholder-zinc-500 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 transition"
                  />
                </div>
              </div>

              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.use_tls}
                  onChange={(e) => handleChange('use_tls', e.target.checked)}
                  className="w-5 h-5 rounded border-zinc-600 bg-zinc-800 text-emerald-500 focus:ring-emerald-500"
                />
                <span className="text-sm text-zinc-300">Utiliser TLS/SSL</span>
              </label>

              {/* Bouton Test Connexion */}
              <div className="pt-4 border-t border-zinc-800">
                {isEditMode && !formData.username && !formData.password && (
                  <div className="mb-3 p-3 rounded-lg bg-blue-900/20 border border-blue-800 text-blue-300 text-sm">
                    Pour tester la connexion, veuillez re-saisir vos identifiants ci-dessous
                  </div>
                )}
                <button
                  type="button"
                  onClick={handleTestConnection}
                  disabled={testing || !formData.imap_host || !formData.username || !formData.password}
                  className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-blue-600 hover:bg-blue-500 text-white font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {testing ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Test en cours...
                    </>
                  ) : (
                    <>
                      <Server className="w-4 h-4" />
                      Tester la connexion
                    </>
                  )}
                </button>
                
                {testResult && (
                  <div className={`mt-3 p-3 rounded-lg border ${
                    testResult.success 
                      ? 'bg-emerald-900/20 border-emerald-800 text-emerald-300' 
                      : 'bg-red-900/20 border-red-800 text-red-300'
                  }`}>
                    <div className="flex items-start gap-2">
                      {testResult.success ? (
                        <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                      ) : (
                        <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                      )}
                      <span className="text-sm">{testResult.message}</span>
                    </div>
                  </div>
                )}
              </div>
            </section>

            {/* Section: Authentification */}
            <section className="space-y-6">
              <div className="flex items-center gap-2 text-emerald-400 mb-4">
                <Lock className="w-5 h-5" />
                <h3 className="text-sm font-semibold uppercase tracking-wider">Authentification</h3>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-2">
                  Nom d'utilisateur {!isEditMode && '*'}
                </label>
                <input
                  type="text"
                  value={formData.username}
                  onChange={(e) => handleChange('username', e.target.value)}
                  placeholder={isEditMode ? '(inchangé)' : 'contact@votredomaine.com'}
                  className="w-full px-4 py-3 rounded-xl bg-zinc-800 border border-zinc-700 text-white placeholder-zinc-500 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 transition"
                />
                {isEditMode && (
                  <p className="mt-1 text-xs text-zinc-500">Laissez vide pour conserver l'actuel</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-2">
                  Mot de passe {!isEditMode && '*'}
                </label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => handleChange('password', e.target.value)}
                  placeholder={isEditMode ? '(inchangé)' : '••••••••'}
                  className="w-full px-4 py-3 rounded-xl bg-zinc-800 border border-zinc-700 text-white placeholder-zinc-500 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 transition"
                />
                {isEditMode && (
                  <p className="mt-1 text-xs text-zinc-500">Laissez vide pour conserver l'actuel</p>
                )}
              </div>
            </section>

            {/* Section: Paramètres (edit mode only) */}
            {isEditMode && (
              <section className="space-y-6">
                <div className="flex items-center gap-2 text-emerald-400 mb-4">
                  <Settings className="w-5 h-5" />
                  <h3 className="text-sm font-semibold uppercase tracking-wider">Paramètres</h3>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-zinc-300 mb-2">
                    Intervalle de vérification (minutes)
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="60"
                    value={formData.check_interval_minutes}
                    onChange={(e) => handleChange('check_interval_minutes', e.target.value)}
                    className="w-full px-4 py-3 rounded-xl bg-zinc-800 border border-zinc-700 text-white placeholder-zinc-500 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 transition"
                  />
                </div>

                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.enabled}
                    onChange={(e) => handleChange('enabled', e.target.checked)}
                    className="w-5 h-5 rounded border-zinc-600 bg-zinc-800 text-emerald-500 focus:ring-emerald-500"
                  />
                  <span className="text-sm text-zinc-300">Connexion activée</span>
                </label>

                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.auto_reply_enabled}
                    onChange={(e) => handleChange('auto_reply_enabled', e.target.checked)}
                    className="w-5 h-5 rounded border-zinc-600 bg-zinc-800 text-emerald-500 focus:ring-emerald-500"
                  />
                  <span className="text-sm text-zinc-300">Réponse automatique IA</span>
                </label>
              </section>
            )}
          </div>

          {/* Info box */}
          <div className="mt-8 p-4 rounded-xl bg-zinc-800/50 border border-zinc-700">
            <h4 className="text-sm font-medium text-zinc-300 mb-2">💡 Configuration IMAP</h4>
            <ul className="text-sm text-zinc-400 space-y-1">
              <li>• Port IMAP standard avec TLS: <strong className="text-zinc-300">993</strong></li>
              <li>• Port SMTP standard avec STARTTLS: <strong className="text-zinc-300">587</strong></li>
              <li>• Pour Gmail, utilisez un <strong className="text-zinc-300">mot de passe d'application</strong></li>
              <li>• Vérifiez que l'accès IMAP est activé dans les paramètres de votre fournisseur email</li>
            </ul>
          </div>
        </div>
      </form>
    </div>
  )
}
